import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUpRight, Check, Phone, Mail, ShieldCheck, FileText, CalendarDays } from 'lucide-react';
import { Header, Footer } from '@/components/public';
import { SEOHead } from '@/components/seo';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { UsEnquiryModal } from '@/components/public/us/UsEnquiryModal';
import { UsSectionHeading, UsEnquireButton } from '@/components/public/us/UsElements';
import { SITE_ORIGIN } from '@/components/public/us/usContent';
import '@/components/public/uk/uk.css';

const PHONE = '+1 (205) 624-5819';
const PHONE_HREF = 'tel:+12056245819';
const EMAIL = 'hi@starteaseai.com';
const CANONICAL = `${SITE_ORIGIN}/usa/bookkeeping/`;
const CTA = 'Request a consultation';

const INCLUDED = [
  ['Transaction categorization', 'Every transaction coded to the right account, each month.'],
  ['Bank & card reconciliations', 'Accounts reconciled to your statements so the numbers tie out.'],
  ['Monthly P&L and balance sheet', 'Clean financial statements in your inbox every month.'],
  ['Certified QuickBooks ProAdvisors', 'Your books kept by certified professionals, not software alone.'],
  ['Catch-up & cleanup', 'We fix and bring current months — or years — of back bookkeeping.'],
  ['A/R and A/P tracking', 'Keep an eye on what you’re owed and what you owe.'],
  ['Year-end, tax-ready books', 'Books closed and handed to your CPA, ready to file.'],
  ['Clean, tied-out audit trail', 'Every figure traces back to your statements.'],
];

const PRICING = [
  ['Hourly bookkeeping', '$50', '/ hour', 'Catch-up, cleanup and as-needed work, billed by the hour with pre-approval on larger jobs.'],
  ['Monthly plan', '$150', '/ month', 'A flat monthly fee for ongoing categorization, reconciliations and reporting — scaled to your transaction volume.'],
];

const WORKFLOW = [
  ['Collect', 'We pull your bank, card and channel activity through secure read-only access.'],
  ['Categorize', 'Every transaction is coded to the right account for the month.'],
  ['Reconcile', 'Accounts are reconciled to your statements so nothing is missed.'],
  ['Report', 'You get a clean monthly P&L and balance sheet.'],
  ['Review', 'We flag anything that needs your eye and keep the books tax-ready.'],
];

const STEPS = [
  ['Discovery call', 'We review your accounts, transaction volume and the state of your books, then confirm your plan.'],
  ['Secure setup', 'You give us read access to your bank, cards and QuickBooks — you keep ownership and the subscription bills to you.'],
  ['Monthly close', 'We categorize, reconcile and report every month, and keep your year-end books tax-ready.'],
];

const FAQS = [
  ['How is bookkeeping priced?', 'As-needed and cleanup work is billed at $50 per hour. Ongoing monthly bookkeeping starts at $150 per month — a flat fee scaled to your transaction volume, which we confirm on a short discovery call so you only pay for what your books actually need.'],
  ['What software do you use?', 'We work in QuickBooks and our team are Certified QuickBooks ProAdvisors. You keep ownership of the account, and the software subscription is billed directly to you at cost — never marked up.'],
  ['Can you clean up months of back bookkeeping?', 'Yes. Catch-up and cleanup of historical books is our specialty, billed at $50 per hour with pre-approval before we start any larger job so there are no surprises.'],
  ['Do you file my taxes?', 'We keep your books accurate and tax-ready and hand them to your CPA to file your income tax. We also offer managed payroll and sales-tax filing as separate services. We are not a law firm and don’t provide income-tax filing or legal advice.'],
  ['How often will I get reports?', 'Every month you receive a profit-and-loss statement and a balance sheet, reconciled to your accounts, with anything that needs your attention flagged.'],
  ['Do I need to log into anything?', 'No. There’s no dashboard for you to operate — we do the work and send you clean monthly reports. You only need to give us read access during setup.'],
];

const NAV = [
  ['included', 'Included'],
  ['pricing', 'Pricing'],
  ['fees', 'How fees work'],
  ['workflow', 'Monthly close'],
  ['onboarding', 'Onboarding'],
  ['questions', 'FAQs'],
];

const card = { border: '1px solid var(--uk-line)', background: 'var(--uk-panel)', borderRadius: 12, padding: '26px 24px' };

export default function USABookkeepingPage() {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.hash === '#enquiry') setEnquiryOpen(true);
    else if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
  }, [location.hash]);

  const enquire = () => setEnquiryOpen(true);
  const changeOpen = (value) => { setEnquiryOpen(value); if (!value && location.hash === '#enquiry') navigate(location.pathname, { replace: true }); };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Managed Bookkeeping Services',
    serviceType: 'Managed US bookkeeping',
    description: 'Done-for-you US bookkeeping by Certified QuickBooks ProAdvisors — monthly categorization, reconciliations and reports, with year-end books kept tax-ready.',
    url: CANONICAL,
    areaServed: 'US',
    provider: { '@type': 'Organization', name: 'StartEase Agent', url: SITE_ORIGIN, telephone: '+1-205-624-5819', email: EMAIL },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
      { '@type': 'ListItem', position: 2, name: 'United States', item: `${SITE_ORIGIN}/usa/` },
      { '@type': 'ListItem', position: 3, name: 'Managed Bookkeeping', item: CANONICAL },
    ],
  };

  return (
    <div className="uk-page" data-testid="us-bookkeeping-page">
      <SEOHead
        title="Managed Bookkeeping Services (US) — Done-for-You, Tax-Ready Books | StartEase Agent"
        description="Done-for-you US bookkeeping by Certified QuickBooks ProAdvisors — monthly categorization, reconciliations and reports, with year-end books kept tax-ready. Hourly or a tailored monthly plan."
        canonical={CANONICAL}
      >
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </SEOHead>
      <Header />

      <main>
        {/* 1. HERO */}
        <section className="uk-hero" data-testid="bookkeeping-hero">
          <div className="uk-wrap" style={{ paddingBlock: '56px 64px' }}>
            <nav className="uk-breadcrumb" aria-label="Breadcrumb" data-testid="bookkeeping-breadcrumb">
              <Link to="/" data-testid="bookkeeping-breadcrumb-home">Home</Link><span>/</span>
              <Link to="/usa/" data-testid="bookkeeping-breadcrumb-usa">United States</Link><span>/</span>
              <span aria-current="page">Managed Bookkeeping</span>
            </nav>
            <div className="uk-hero-copy" style={{ maxWidth: 780 }}>
              <div className="uk-eyebrow">United States<span className="uk-sep" />Managed bookkeeping</div>
              <h1 data-testid="bookkeeping-hero-title">Clean books, <em>kept for you.</em></h1>
              <p data-testid="bookkeeping-hero-description">
                Certified QuickBooks ProAdvisors keep your US books accurate and tax-ready every month —
                categorization, reconciliations and clear financial reports, done for you.
              </p>
              <div className="uk-hero-actions">
                <UsEnquireButton onClick={enquire} testId="bookkeeping-hero-cta">{CTA}</UsEnquireButton>
                <a href="#pricing" className="uk-text-link" data-testid="bookkeeping-hero-pricing">See pricing<ArrowDown size={16} /></a>
              </div>
              <span className="uk-hero-footnote" data-testid="bookkeeping-hero-footnote">
                Certified QuickBooks ProAdvisors <span> / </span> Monthly reports <span> / </span> Books kept tax-ready
              </span>
            </div>
          </div>
        </section>

        {/* Trust facts */}
        <div className="uk-facts-band"><div className="uk-wrap uk-facts" data-testid="bookkeeping-facts">
          {[[ShieldCheck, 'ProAdvisor-led', 'Your books kept by Certified QuickBooks ProAdvisors.'],
            [FileText, 'Monthly reports', 'A clean P&L and balance sheet in your inbox every month.'],
            [CalendarDays, 'Tax-ready', 'Year-end books closed and ready to hand to your CPA.']].map(([Icon, title, text], i) => (
            <div key={title} data-testid={`bookkeeping-fact-${i}`}><Icon size={23} strokeWidth={1.4} /><div><strong>{title}</strong><p>{text}</p></div></div>
          ))}
        </div></div>

        {/* Section nav */}
        <nav className="uk-wrap uk-section-nav" aria-label="On this page" data-testid="bookkeeping-section-nav">
          {NAV.map(([id, text], i) => <a key={id} href={`#${id}`} data-testid={`bookkeeping-nav-${id}`}><span>0{i + 1}</span>{text}</a>)}
        </nav>

        {/* 2. WHAT'S INCLUDED */}
        <section id="included" className="uk-section uk-wrap" data-testid="bookkeeping-included-section">
          <UsSectionHeading id="bookkeeping-included" number="01" eyebrow="The managed scope" title="Your books, handled every month." text="Everything a clean set of books needs — kept current by a real team, not software alone." />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '18px 40px', marginTop: 10 }} data-testid="bookkeeping-included-grid">
            {INCLUDED.map(([title, text], i) => (
              <div key={title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', paddingBlock: 14, borderTop: '1px solid var(--uk-line)' }} data-testid={`bookkeeping-included-${i}`}>
                <Check size={18} style={{ color: 'var(--uk-green)', flexShrink: 0, marginTop: 4 }} />
                <div><strong style={{ display: 'block', color: '#fff', fontSize: 15, fontWeight: 600 }}>{title}</strong><p style={{ color: 'var(--uk-ink)', fontSize: 13.5, marginTop: 4 }}>{text}</p></div>
              </div>
            ))}
          </div>
        </section>

        {/* 3. PRICING */}
        <section id="pricing" className="uk-soft-band"><div className="uk-wrap uk-section" data-testid="bookkeeping-pricing-section">
          <div className="uk-heading-row">
            <UsSectionHeading id="bookkeeping-pricing" number="02" eyebrow="Priced to your books" title="Hourly, or a monthly plan." text="These are StartEase management fees. Your QuickBooks subscription is billed to you directly, at cost — never marked up." />
            <UsEnquireButton secondary onClick={enquire} testId="bookkeeping-pricing-cta">{CTA}</UsEnquireButton>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 20, marginTop: 14 }} data-testid="bookkeeping-prices">
            {PRICING.map(([title, price, unit, text], i) => (
              <div key={title} style={card} data-testid={`bookkeeping-price-${i}`}>
                <h3 style={{ color: '#fff', fontSize: 18 }}>{title}</h3>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 10 }}>
                  <span style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 36, color: '#fff', lineHeight: 1 }}>{price}</span>
                  <span style={{ color: '#6ee7b7', fontSize: 13 }}>{unit}</span>
                </div>
                <p style={{ color: 'var(--uk-ink)', fontSize: 13, marginTop: 14 }}>{text}</p>
              </div>
            ))}
          </div>
          <p className="uk-fineprint" style={{ marginTop: 22 }} data-testid="bookkeeping-pricing-note">
            We confirm your monthly plan on a short discovery call, scaled to your transaction volume — so you only pay for what your books actually need.
          </p>
        </div></section>

        {/* 4. HOW FEES WORK */}
        <section id="fees" className="uk-section uk-wrap" data-testid="bookkeeping-fees-section">
          <UsSectionHeading id="bookkeeping-fees" number="03" eyebrow="Two separate numbers" title="Our fee vs. your software." text="We keep our bookkeeping fee cleanly separate from the QuickBooks subscription you pay directly." />
          <div className="uk-responsibilities">
            <div>
              <span className="uk-eyebrow">StartEase<span className="uk-sep" />our management fee</span>
              <h3>You pay us</h3>
              <ul>
                {['Hourly bookkeeping for catch-up, cleanup and as-needed work', 'Or a flat monthly fee tailored to your transaction volume', 'Monthly categorization, reconciliations and reporting', 'Year-end books closed and made tax-ready'].map((text, i) => (
                  <li key={text} data-testid={`bookkeeping-fee-us-${i}`}><Check size={17} /><span>{text}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <span className="uk-eyebrow">Paid directly<span className="uk-sep" />software</span>
              <h3>You pay at cost</h3>
              <ul>
                {['Your QuickBooks subscription, billed to your card at cost — never marked up', 'You keep ownership of the account and your data', 'Any add-on apps you choose to connect', 'Your CPA’s fee to file the income-tax return'].map((text, i) => (
                  <li key={text} data-testid={`bookkeeping-fee-client-${i}`}><Check size={17} /><span>{text}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 5. MONTHLY CLOSE WORKFLOW */}
        <section id="workflow" className="uk-dark-band"><div className="uk-wrap uk-section" data-testid="bookkeeping-workflow-section">
          <UsSectionHeading id="bookkeeping-workflow" number="04" eyebrow="Every month" title="The monthly close." text="A repeatable rhythm that keeps your books clean and your reports on time." />
          <ol className="pay-steps" data-testid="bookkeeping-workflow-steps">
            {WORKFLOW.map(([title, text], i) => (
              <li key={title} data-testid={`bookkeeping-workflow-${i + 1}`}><span className="pay-step-num">0{i + 1}</span><h3>{title}</h3><p>{text}</p></li>
            ))}
          </ol>
        </div></section>

        {/* 6. ONBOARDING */}
        <section id="onboarding" className="uk-soft-band"><div className="uk-wrap uk-section" data-testid="bookkeeping-onboarding-section">
          <UsSectionHeading id="bookkeeping-onboarding" number="05" eyebrow="From sign-up to hands-free" title="Three steps to clean books." />
          <ol className="pay-steps" data-testid="bookkeeping-steps">
            {STEPS.map(([title, text], i) => (
              <li key={title} data-testid={`bookkeeping-step-${i + 1}`}><span className="pay-step-num">0{i + 1}</span><h3>{title}</h3><p>{text}</p></li>
            ))}
          </ol>
        </div></section>

        {/* 7. FAQ */}
        <section id="questions" className="uk-section uk-wrap" data-testid="bookkeeping-faq-section">
          <UsSectionHeading id="bookkeeping-faq" number="06" eyebrow="Questions, answered" title="Bookkeeping, made clear." text="What founders and finance teams ask us most." />
          <Accordion type="single" collapsible className="uk-faqs" data-testid="bookkeeping-faqs">
            {FAQS.map(([question, answer], i) => (
              <AccordionItem key={question} value={`question-${i}`} data-testid={`bookkeeping-faq-item-${i}`}>
                <AccordionTrigger data-testid={`bookkeeping-faq-trigger-${i}`}>{question}</AccordionTrigger>
                <AccordionContent data-testid={`bookkeeping-faq-answer-${i}`}>{answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* 8. CLOSING CTA */}
        <section className="uk-section uk-wrap uk-closing" data-testid="bookkeeping-closing-section">
          <div>
            <span className="uk-eyebrow">Done-for-you bookkeeping.</span>
            <h2>Let’s get your books in order.</h2>
            <p>Tell us about your accounts and volume — we’ll confirm your plan and keep your books clean every month.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px', marginTop: 18, fontSize: 14 }}>
              <a href={PHONE_HREF} className="uk-text-link" data-testid="bookkeeping-closing-phone"><Phone size={15} />{PHONE}</a>
              <a href={`mailto:${EMAIL}`} className="uk-text-link" data-testid="bookkeeping-closing-email"><Mail size={15} />{EMAIL}</a>
            </div>
          </div>
          <UsEnquireButton onClick={enquire} testId="bookkeeping-closing-cta">{CTA}</UsEnquireButton>
        </section>

        <div className="uk-wrap uk-editorial-note" data-testid="bookkeeping-editorial-note">
          <p>General information, not legal or tax advice. We keep books on the client’s behalf as a managed service; we are not a law firm and do not file income-tax returns. Scope, inclusions and fees are confirmed in writing before engagement, and businesses remain responsible for their legal obligations.</p>
        </div>
      </main>

      <Footer />
      <UsEnquiryModal open={enquiryOpen} onOpenChange={changeOpen} structure="Existing US company support" />
    </div>
  );
}
